import { useTranslation } from "react-i18next";
import { FormProvider, useFormContext } from "react-hook-form";

import { ErrorAlert } from "./components/ErrorAlert.jsx";
import { useStatusLookup } from "./useStatusLookup.js";

export function StatusLookupStep({ routes }) {
  const { t } = useTranslation();
  const lookup = useStatusLookup(routes, t);
  const { error, form, indexWithQuery, loading, submit, withdrawal } = lookup;

  if (withdrawal) {
    const lineItems = Array.isArray(withdrawal.line_items) ? withdrawal.line_items : [];
    const statusLabel = t(withdrawal.status_label_key || `withdrawals.status.${withdrawal.status}`, {
      defaultValue: withdrawal.status,
    });
    const statusHelpKey = withdrawal.help_key || `proxy.withdrawals.status.help.${withdrawal.status}`;
    const statusHelp = t(statusHelpKey, {
      defaultValue: t("proxy.withdrawals.status.default_help"),
    });

    return (
      <div className="wp:mx-auto wp:max-w-xl">
        <div className="wp:rounded-md wp:border wp:border-solid wp:border-gray-200 wp:[background-color:var(--wf-bg-color,#ffffff)] wp:p-6 wp:shadow-sm">
          <p className="wp:mb-2 wp:text-sm wp:font-medium wp:text-gray-500">
            {t("proxy.withdrawals.status.current_status")}
          </p>
          <h1 className="wp:mb-4 wp:text-3xl wp:font-normal">{statusLabel}</h1>
          <p className="wp:mb-8 wp:rounded-md wp:bg-gray-50 wp:p-4 wp:text-base">{statusHelp}</p>
          <dl className="wp:grid wp:gap-6 wp:sm:grid-cols-2">
            <Summary label={t("proxy.withdrawals.common.fields.reference_number")}>{withdrawal.token}</Summary>
            <Summary label={t("proxy.withdrawals.common.fields.submitted_at")}>
              {withdrawal.submitted_at_formatted}
            </Summary>
            <Summary label={t("proxy.withdrawals.common.fields.updated_at")}>
              {withdrawal.updated_at_formatted}
            </Summary>
            <Summary label={t("proxy.withdrawals.common.fields.order_number")}>
              {withdrawal.order_reference}
            </Summary>
          </dl>
          {withdrawal.rejection_reason ? (
            <p className="wp:mt-6 wp:rounded-md wp:border wp:border-solid wp:border-red-200 wp:bg-red-50 wp:p-4 wp:text-red-800">
              {withdrawal.rejection_reason}
            </p>
          ) : null}
          {lineItems.length > 0 || withdrawal.items_description ? (
            <div className="wp:mt-8 wp:border-t wp:border-solid wp:border-gray-200 wp:pt-6">
              <h2 className="wp:mb-4 wp:text-xl wp:font-normal">
                {t("proxy.withdrawals.status.submitted_items")}
              </h2>
              {lineItems.length > 0 ? (
                <ul className="wp:m-0 wp:grid wp:gap-4 wp:p-0">
                  {lineItems.map((item) => (
                    <li key={`${item.title}-${item.quantity}`} className="wp:rounded-md wp:bg-gray-50 wp:p-4">
                      <strong>
                        {item.quantity} x {item.title}
                      </strong>
                      {item.variant_title ? <span className="wp:block">{item.variant_title}</span> : null}
                      {item.sku ? <span className="wp:block">SKU: {item.sku}</span> : null}
                      {item.reason_key ? (
                        <span className="wp:block">
                          {t(`withdrawal_reasons.${item.reason_key}`, { defaultValue: item.reason_key })}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="wp:rounded-md wp:bg-gray-50 wp:p-4 wp:whitespace-pre-wrap">
                  {withdrawal.items_description}
                </p>
              )}
            </div>
          ) : null}
        </div>
        <a className="wp:mt-8 wp:block wp:text-center wp:font-medium wp:[color:var(--wf-text-color,#111827)]" href={indexWithQuery}>
          {t("proxy.withdrawals.common.actions.submit_new")}
        </a>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <StatusLookupForm error={error} loading={loading} onSubmit={submit} />
    </FormProvider>
  );
}

function StatusLookupForm({ error, loading, onSubmit }) {
  const { t } = useTranslation();
  const { handleSubmit, register } = useFormContext();

  return (
    <div className="wp:mx-auto wp:max-w-xl">
      <ErrorAlert error={error} />
      <div className="wp:mb-8 wp:rounded-md wp:border wp:border-solid wp:border-gray-200 wp:bg-gray-50 wp:p-6">
        <p className="wp:mb-3 wp:text-sm wp:font-medium wp:uppercase wp:tracking-widest wp:text-gray-500">
          {t("proxy.withdrawals.status.lookup_label")}
        </p>
        <h1 className="wp:mb-4 wp:text-3xl wp:font-normal wp:tracking-tight">
          {t("proxy.withdrawals.status.lookup_title")}
        </h1>
        <p className="wp:text-lg wp:text-gray-600">{t("proxy.withdrawals.status.lookup_description")}</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Field
          id="widerruf-status-token"
          label={t("proxy.withdrawals.common.fields.reference_number")}
          help={t("proxy.withdrawals.status.reference_help")}
          registration={register("token", { required: true })}
        />
        <Field
          id="widerruf-status-email"
          label={t("proxy.withdrawals.common.fields.email")}
          type="email"
          registration={register("customer_email", { required: true })}
        />
        <button
          type="submit"
          disabled={loading}
          className="wp:w-full wp:rounded-md wp:border wp:border-solid wp:[border-color:var(--wf-button-border,#111827)] wp:[background-color:var(--wf-button-bg,#111827)] wp:px-6 wp:py-4 wp:text-lg wp:font-medium wp:[color:var(--wf-button-text,#ffffff)] wp:transition-opacity wp:enabled:cursor-pointer wp:hover:opacity-90 wp:disabled:cursor-not-allowed"
        >
          {t("proxy.withdrawals.common.actions.show_status")}
        </button>
      </form>
    </div>
  );
}

function Field({ id, label, help, registration, type = "text" }) {
  return (
    <div className="wp:mb-6">
      <label htmlFor={id} className="wp:mb-2 wp:block wp:text-base wp:font-medium">
        {label} <span className="wp:text-red-600">*</span>
      </label>
      {help ? <span className="wp:mb-3 wp:block wp:text-sm wp:text-gray-500">{help}</span> : null}
      <input
        id={id}
        type={type}
        required
        {...registration}
        className="wp:block wp:w-full wp:rounded-md wp:border wp:border-solid wp:border-gray-300 wp:[background-color:var(--wf-input-bg,#ffffff)] wp:px-4 wp:py-3 wp:text-lg"
      />
    </div>
  );
}

function Summary({ label, children }) {
  return (
    <div>
      <dt className="wp:text-sm wp:font-medium wp:text-gray-500">{label}</dt>
      <dd className="wp:mt-1 wp:text-lg">{children}</dd>
    </div>
  );
}
